import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { clearIndicators } from "../../../redux/slices/indicatorDisplay/indicator/indicatorSlice";
import { GetIndicatorsQuery } from "../../../redux/slices/indicatorDisplay/indicator/indicatorThunk";
import useUrlParams from "../../../hooks/useUrlParams";
import { scrollToElement } from "../../../utils/utils";
import { SEARCH_MODE_EXPANDED, SEARCH_MODE_PREDICTIVE } from "../../../utils/const";
import { getParamsToClean, idsFromDraft, idsFromUrl, idsToParams, idsToQuery } from "../utils/filterParams";
import {
    countDraft,
    draftFromIds,
    DraftSources,
    EMPTY_DRAFT,
    FilterDraft,
    isSameDraft,
    removeFromDraft,
    toAppliedGroups,
} from "../utils/filterDraft";

interface UseAppliedFiltersParams {
    draft: FilterDraft;
    updateDraft: (draft: FilterDraft) => void;
    resetDraft: () => void;
    searchValue: string;
    clearSearchInput: () => void;
    fetchIndicators: (query: Partial<GetIndicatorsQuery>) => void;
}

// Filtros aplicados (los que están en la URL): pills, aplicar, quitar y limpiar
export const useAppliedFilters = ({ draft, updateDraft, resetDraft, searchValue, clearSearchInput, fetchIndicators }: UseAppliedFiltersParams) => {
    const dispatch = useAppDispatch();
    const { getParam, getMultipleParams, updateParams } = useUrlParams();
    const { rights, ODSOptions, priorityGroupsOptions } = useAppSelector((state) => state.indicator_display_filter);

    const filterSources: DraftSources = {
        rights,
        odsOptions: ODSOptions ?? [],
        priorityGroupsOptions: priorityGroupsOptions ?? [],
    };

    const getUrlSelection = () => draftFromIds(idsFromUrl(getMultipleParams), filterSources);

    // PILLS
    const isPredictiveSearch = getParam("searchMode") === SEARCH_MODE_PREDICTIVE;
    const applied = isPredictiveSearch ? EMPTY_DRAFT : getUrlSelection();
    const appliedGroups = toAppliedGroups(applied);

    const hasCleanableFilters = appliedGroups.some(group => group.values.length > 0);

    // "Aplicar filtros" solo se habilita si hay algo elegido (derechos, ODS o grupos prioritarios)
    // y el borrador del modal difiere de lo aplicado
    const canApplyDraft = countDraft(draft) > 0 && !isSameDraft(draft, applied);

    const applyDraft = (draftToApply: FilterDraft) => {
        if (countDraft(draftToApply) === 0) return handleClean();

        const ids = idsFromDraft(draftToApply, filterSources);

        updateParams({
            page: "1",
            filterApplied: "true",
            ...idsToParams(ids),
            // Solo limpia search si es predictive
            ...(isPredictiveSearch && { searchMode: null, search: null }),
        });

        if (isPredictiveSearch) clearSearchInput();

        scrollToElement("indicator-cards", -50, 1500);

        fetchIndicators({ ...idsToQuery(ids), search: isPredictiveSearch ? undefined : searchValue });
    };

    const applyFilter = () => applyDraft(draft);

    // limpia los filtros
    const handleClean = () => {
        resetDraft();
        updateParams({ ...getParamsToClean("all-filters"), filterApplied: null });

        if (getParam("searchMode") === SEARCH_MODE_EXPANDED) {
            fetchIndicators({ search: getParam("search") ?? "" });
        }
        dispatch(clearIndicators());
    };

    // X de una pill: quita ese valor de lo aplicado y vuelve a filtrar
    const removeAppliedFilter = (key: keyof FilterDraft, label: string) => {
        const newDraft = removeFromDraft(applied, key, label, rights);
        applyDraft(newDraft);
    };

    // Al abrir el modal, el borrador arranca con lo que está en la URL
    const loadUrlIntoDraft = () => updateDraft(getUrlSelection());

    return {
        appliedGroups,
        hasCleanableFilters,
        canApplyDraft,
        applyFilter,
        handleClean,
        removeAppliedFilter,
        loadUrlIntoDraft,
    };
};
